import React, {useCallback, useMemo} from 'react';

import {Content, DropDown, Trigger, useDropDownContext} from '../drop-down';
import {List, ListGroup, ListItem, ListValue} from '../list';
import {Field} from '../inputs/field';
import {Button} from '../inputs/button';
import {ChevronDownIcon, CrossIcon} from '../svg-icon';
import {InputAppearance, InputShape, InputSize} from '../inputs/common-input-interface';
import {cls, humanJoin, wordByCount} from '../../utils';

import css from './select.module.css';
import fieldCss from '../inputs/field/field.module.css';

/** всё поле раскрывает список, а не только текст в нём: пустое место справа, стрелка и края тоже */
const SelectBox: React.FC<{children: React.ReactNode}> = ({children}) => {
	const drop = useDropDownContext();

	const handleClick = useCallback(
		(event: React.MouseEvent) => {
			if (!drop || drop.disabled || event.defaultPrevented) return;
			// кнопки поля — сам выбор и «очистить» — щелчок обрабатывают сами
			if ((event.target as HTMLElement).closest('button')) return;
			drop.toggle(event);
			drop.triggerRef.current?.focus();
		},
		[drop],
	);

	return (
		<div className={css.box} onClick={handleClick}>
			{children}
		</div>
	);
};

export type SelectItem = {
	value: ListValue;
	label: React.ReactNode;
	/** подпись в самом поле, если она отличается от подписи пункта */
	title?: string;
	disabled?: boolean;
	/** заголовок группы в списке: пункты одной группы должны идти подряд */
	group?: string;
};

export type SelectProps = {
	items: SelectItem[];
	/** выбранные значения; одиночный выбор принимает и одно значение */
	value?: ListValue | ListValue[] | null;
	onChange(value: ListValue[], event: React.SyntheticEvent): void;
	multiple?: boolean;
	placeholder?: string;
	label?: string;
	error?: string | boolean;
	/** рамка акцентного цвета: здесь ещё нужно сделать выбор */
	highlighted?: boolean;
	helperText?: string;
	bottomText?: string;
	disabled?: boolean;
	/** убрать крестик очистки */
	disableClear?: boolean;
	appearance?: InputAppearance;
	size?: InputSize;
	shape?: InputShape;
	className?: string;
	name?: string;
};

const renderItem = (item: SelectItem) => (
	<ListItem key={item.value} value={item.value} disabled={item.disabled}>
		{item.label}
	</ListItem>
);

/** пункты под заголовками групп; соседние пункты одной группы собираются вместе */
function renderItems(items: SelectItem[]) {
	if (!items.some((item) => item.group)) return items.map(renderItem);
	const groups: {title?: string; items: SelectItem[]}[] = [];
	for (const item of items) {
		const last = groups[groups.length - 1];
		if (last && last.title === item.group) last.items.push(item);
		else groups.push({title: item.group, items: [item]});
	}
	return groups.map((group, index) =>
		group.title === undefined ? (
			group.items.map(renderItem)
		) : (
			<ListGroup key={`${group.title}-${index}`} title={group.title}>
				{group.items.map(renderItem)}
			</ListGroup>
		),
	);
}

const titleOf = (item: SelectItem) => item.title ?? (typeof item.label === 'string' ? item.label : String(item.value));

export const Select: React.FC<SelectProps> = ({
	items,
	value,
	onChange,
	multiple,
	placeholder,
	label,
	error,
	highlighted,
	helperText,
	bottomText,
	disabled,
	disableClear,
	appearance = 'outline',
	size,
	shape,
	className,
	name,
}) => {
	const selected = useMemo(() => (value == null ? [] : Array.isArray(value) ? value : [value]), [value]);

	const text = useMemo(() => {
		const chosen = items.filter((item) => selected.includes(item.value));
		if (!chosen.length) return '';
		if (chosen.length < 4) return humanJoin(chosen.map(titleOf));
		return `${titleOf(chosen[0])} и еще ${wordByCount(
			chosen.length - 1,
			['значение', 'значения', 'значений'],
			true,
		)}`;
	}, [items, selected]);

	const handleClear = useCallback((event: React.MouseEvent) => onChange([], event), [onChange]);

	return (
		<DropDown
			selectMode={multiple ? 'multiple' : 'single'}
			value={selected}
			onChange={onChange}
			disabled={disabled}
			className={cls(css.select, className)}
		>
			<SelectBox>
				<Field
					commonProps={{label, error, helperText, bottomText, disabled}}
					topLabel={!!label && (!!text || !!placeholder)}
					highlighted={highlighted}
					appearance={appearance}
					size={size}
					shape={shape}
					buttons={
						<>
							{!!selected.length && !disabled && !disableClear && (
								<Button
									appearance="subtle"
									icon={<CrossIcon width={14} height={14} />}
									onClick={handleClear}
									aria-label="Очистить"
									className={fieldCss.actionButton}
								/>
							)}
							<ChevronDownIcon className={css.chevron} width={14} height={14} />
						</>
					}
				>
					<Trigger>
						<button type="button" className={css.trigger} disabled={disabled} name={name}>
							{text || <span className={css.placeholder}>{placeholder}</span>}
						</button>
					</Trigger>
				</Field>
			</SelectBox>

			<Content width="trigger">
				<List selectAppearance={multiple ? 'checkbox' : 'selected'} aria-label={label}>
					{renderItems(items)}
				</List>
			</Content>
		</DropDown>
	);
};
